// src/components/Dialogs/FeedbackDialog.tsx
import React, { useState } from "react";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Box,
  Typography,
  TextField,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import FeedbackOutlinedIcon from "@mui/icons-material/FeedbackOutlined";
import Rating from "@mui/material/Rating";

interface FeedbackMsgDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (rating: number, comment: string, msgId: string | null) => void;
  msgId: string | null;
}

export const FeedbackMsgDialog: React.FC<FeedbackMsgDialogProps> = ({
  open,
  onClose,
  onSubmit,
  msgId,
}) => {
  const theme = useTheme();
  const [rating, setRating] = useState<number | null>(0); // Default 0 stars
  const [comment, setComment] = useState<string>("");

  const handleRatingChange = (
    event: React.ChangeEvent<{}>,
    newValue: number | null
  ) => {
    setRating(newValue);
  };

  const handleCommentChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setComment(event.target.value);
  };

  const handleSubmit = () => {
    if (rating !== null && rating > 0) {
      onSubmit(rating, comment, msgId);

      setRating(0);
      setComment("");
    } else {
      // Optionally, handle validation if rating is 0 or no comment
      alert("Please provide a rating before submitting.");
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        style: {
          backgroundColor: theme.palette.background.paper,
          background: theme.palette.background.paper,
          padding: "16px",
        },
      }}
    >
      <DialogTitle>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
        >
          <FeedbackOutlinedIcon
            style={{ marginBottom: 16, color: theme.palette.primary.main }}
          />
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            ให้คำติชม
          </Typography>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          flexDirection="column"
          sx={{ mt: 2 }}
        >
          {/* Star Rating Component */}
          <Rating
            name="feedback-rating"
            value={rating}
            onChange={handleRatingChange}
            precision={0.5} // Allows half-star ratings
            size="large"
          />

          {/* Comment TextField */}
          <TextField
            margin="dense"
            label="ความคิดเห็น"
            fullWidth
            multiline
            rows={4}
            value={comment}
            onChange={handleCommentChange}
            sx={{ mt: 2 }}
          />

          {/* Message Id */}
          <Box
            display="flex"
            flexDirection="column"
            gap={0.5}
            sx={{
              mt: 2,
              width: "100%",
            }}
          >
            <Typography variant="body2" color="textSecondary">
              รหัสอ้างอิง:
            </Typography>
            <Typography
              variant="body2"
              color="textPrimary"
              sx={{ fontWeight: 600 }}
            >
              {msgId}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Box display="flex" justifyContent="center" width="100%" gap={2}>
          <Button
            onClick={onClose}
            color="primary"
            variant="outlined"
            fullWidth
          >
            ยกเลิก
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            fullWidth
          >
            ส่งคำติชม
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};
